import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet } from "@/lib/api";
import { formatCurrency } from "@/lib/commission";

type Row = {
  rank: number;
  user_id: string;
  coupon: string;
  month_year: string;
  name: string | null;
  email: string | null;
  role: string | null;
  total_sales: number;
  orders_count: number;
  commission_amount: number;
  commission_rate: number;
  total_sales_after_shipping: number;
  shipping_fees: number;
  period_start: string;
  period_end: string;
  updated_at: string;
};

export default function AdminMonthlyLeaderboard() {
  const [monthYear, setMonthYear] = useState<string>("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (overrideMonth?: string) => {
    setLoading(true);
    try {
      const url = new URL("/api/commission-balances/list", window.location.origin);
      if (overrideMonth || monthYear) {
        url.searchParams.set("month_year", (overrideMonth || monthYear).trim());
      }
      const res = await apiGet(url.pathname + url.search);
      if (!res.ok) {
        throw new Error(await res.text());
      }
      const data = await res.json();
      setMonthYear(data.month_year);
      setRows(data.results || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Ranking mensal (balances)</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              value={monthYear}
              onChange={(e) => setMonthYear(e.target.value)}
              placeholder="YYYY-MM"
              className="w-32"
            />
            <Button onClick={() => fetchData()}>Carregar</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 px-2">#</th>
                <th className="py-2 px-2">Cupom</th>
                <th className="py-2 px-2">Nome</th>
                <th className="py-2 px-2">Vendas</th>
                <th className="py-2 px-2">Pedidos</th>
                <th className="py-2 px-2">Comissão</th>
                <th className="py-2 px-2">Taxa</th>
                <th className="py-2 px-2">Período</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-3 px-2" colSpan={8}>
                    Carregando...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="py-3 px-2" colSpan={8}>
                    Sem dados
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={`${r.user_id}-${r.coupon}`} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-2 font-medium">{r.rank}</td>
                    <td className="py-2 px-2 font-mono">{r.coupon}</td>
                    <td className="py-2 px-2">{r.name || "-"}</td>
                    <td className="py-2 px-2">{formatCurrency(r.total_sales)}</td>
                    <td className="py-2 px-2">{r.orders_count}</td>
                    <td className="py-2 px-2">{formatCurrency(r.commission_amount)}</td>
                    <td className="py-2 px-2">{Math.round(r.commission_rate * 100)}%</td>
                    <td className="py-2 px-2">
                      {r.period_start} — {r.period_end}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
